import { type IkeFieldType, IkeToken } from '@ikegps/core-api';
import type { FormModelField, FormModelFieldSubForm } from '@ikegps/core-shared';
import { action, observable, makeObservable } from 'mobx';
import { Icon } from '../../../components/icon';
import { MenuComponent } from '../../../components/menu/menu';
import { type MenuItem, MenuItemComponent } from '../../../components/menu/menu.item';
import { CSS as MenuItemCSS } from '../../../components/menu/menu.item.style';
import { addStyles, DefaultTheme } from '../../../components/theme';
import { T, Translate } from '../../../i18n';
import { m, type MithrilVNode } from '../../../mithril';
import { Services } from '../../../services';
import type { FormDesigner } from '../form.designer';
import { getFieldTypeDisplay } from '../form.palette';
import { FormFieldEditor } from './form.field.editor';
import { SubformEditorDropdown, SubformEditorDropdownCSS } from './subform/form.field.editor.subform.dropdown';

const CSS = {
	container: 'c-FdcHeight',
	list: 'c-FdcHeight__list',
	menu: 'c-FdcHeight__menu',
	option: 'c-FdcHeight__option',
	value: 'c-FdcHeight__value',
	ghost: 'c-FdcHeight__ghost',
	selected: 'c-FdcHeight__selected',
};

addStyles({
	[CSS.container]: {
		width: '100%',
		display: 'flex',
		'flex-direction': 'column',
		'& .mdl-button': {
			height: 'fit-content',
			width: 'fit-content',
		},
	},
	[CSS.list]: {
		display: 'flex',
		width: '100%',
		'align-items': 'center',
	},
	[CSS.option]: {
		width: '320px',
		'text-align': 'left',
		display: 'flex',
		'align-items': 'center',
	},
	[CSS.selected]: {
		width: '320px',
		'text-align': 'left',
		display: 'flex',
		'align-items': 'center',
	},
	[CSS.ghost]: {
		color: DefaultTheme.font.color.supporting,
	},
	[CSS.value]: {
		width: '520px', // TODO this is from the input width
		'text-align': 'left',
	},
	[CSS.menu]: {
		[`& .${MenuItemCSS.item}`]: {
			'padding-top': DefaultTheme.space(1),
			'padding-bottom': DefaultTheme.space(1),
			'line-height': DefaultTheme.space(6),
		},
	},
});

interface HeightFieldSelectOptions {
	fieldId: string;
	displayName: string;
	fieldType?: string;
	parentDisplayName?: string;
}

export class FormFieldEditorHeight extends FormFieldEditor<FormModelField> {
	annotationMenu: MenuComponent;
	@observable annotationLabel: HeightFieldSelectOptions | null = null;
	list: HeightFieldSelectOptions[];

	constructor(designer: FormDesigner, field: FormModelField) {
		super(designer, field);

		makeObservable(this);

		this.list = this.getListOfTitleFields(this.field.parent);
		this.list.unshift({ displayName: 'Default', fieldId: null, fieldType: null });

		this.annotationMenu = new MenuComponent({
			items: () => this.viewListItems(),
			className: CSS.menu,
		});

		this.annotationLabel = this.list.find((l) => l.fieldId === this.field.annotationLabelFieldId) || this.list[0];
	}

	protected getListOfTitleFields(subform: FormModelFieldSubForm) {
		let list: HeightFieldSelectOptions[] = [];
		if (subform.parent != null) {
			list.push(...this.getListOfTitleFields(subform.parent));
		}
		list.push(
			...subform.fields.map((x) => {
				let item: HeightFieldSelectOptions = {
					displayName: x.displayName,
					fieldId: x.fieldId.split(':').pop(),
					fieldType: x.type,
					parentDisplayName: x.parent.parent ? x.parent.displayName : undefined,
				};
				if (x.fieldId === this.field.fieldId) {
					item.displayName = 'Self-Title (' + x.displayName + ')';
				}
				return item;
			})
		);
		return list;
	}

	protected getIcon(icon: string | null) {
		return icon
			? Icon.view(getFieldTypeDisplay(icon as IkeFieldType).icon, {
					className: SubformEditorDropdownCSS.iconWrapper,
				})
			: m(
					'i',
					{
						className: `${SubformEditorDropdownCSS.iconLabel} ${SubformEditorDropdownCSS.iconWrapper}`,
						title: Translate(T.FORM_OPTION_LABEL),
					},
					'L'
				);
	}

	metadataIcon(iconText: string): MithrilVNode {
		return m(
			'i',
			{ className: `${SubformEditorDropdownCSS.iconLabel} ${SubformEditorDropdownCSS.iconWrapper}` },
			iconText
		);
	}
	protected getLabelWithNode() {
		if (Services.Token.has(IkeToken.FEATURE_MARKER_LABEL)) {
			const selectedText = this.annotationLabel.parentDisplayName
				? `${this.annotationLabel.parentDisplayName} > ${this.annotationLabel.displayName}`
				: this.annotationLabel.displayName;

			return SubformEditorDropdown.view({
				title: 'Label With',
				selectedText,
				menu: this.annotationMenu,
				icon: this.getIcon(this.annotationLabel.fieldType),
			});
		}

		return null;
	}

	//default view
	override viewInfoRW() {
		const node = m('div', { className: CSS.list }, [this.getLabelWithNode()]);

		return node;
	}

	viewListItems(): MenuItem[] {
		const items: MenuItem[] = [];
		for (let i = 0; i < this.list.length; i++) {
			const option = this.list[i];
			items.push({
				view: MenuItemComponent.view([
					this.getIcon(option.fieldType),
					option.parentDisplayName ? `${option.parentDisplayName} > ${option.displayName}` : option.displayName,
				]),
				action: () => this.changeAnnotationField(option),
			});
		}

		return items;
	}

	@action.bound protected changeAnnotationField(key: HeightFieldSelectOptions): any {
		this.setAnnotationLabelFieldId(key.fieldId);
		this.annotationLabel = key;
	}
}
